import { parse as parseQuery } from 'query-string';

import { ROUTES } from 'src/routes';

export interface ImageDimensions {
  width: number;
  height: number;
}

const WRAPPER_STYLE = `
  position: absolute; 
  top: -10000px; 
  left: 0;
`;

export const calculateBlockHeight = (content: string, style: string): number => {
  const div = document.createElement('div');
  div.setAttribute('style', style);
  div.innerHTML = content;

  const wrapper = document.createElement('div');
  wrapper.setAttribute('style', WRAPPER_STYLE);
  wrapper.appendChild(div);

  document.body.appendChild(wrapper);
  const height = div.offsetHeight;
  document.body.removeChild(wrapper);

  return height;
};

const CALCULATE_IMAGE_SIZE_TIMEOUT = 1000;
export const calculateImageDimensionsUrl = (imageUrl: string): Promise<ImageDimensions> =>
  new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      resolve({
        width: image.width,
        height: image.height,
      });
    };

    image.onerror = () => {
      reject(new Error('Unable to get image dimensions.'));
    };

    image.src = imageUrl;

    setTimeout(
      () => reject(new Error('Timeout in calculating image dimensions')),
      CALCULATE_IMAGE_SIZE_TIMEOUT
    );
  });

export const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event: ProgressEvent<FileReader>) => {
      const result = event.target?.result;
      if (typeof result === 'string') {
        resolve(result);
      } else {
        reject(new Error('Unable to read file.'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Unable to read file.'));
    };

    reader.readAsDataURL(file);
  });

export const calculateImageDimensionsFile = async (file: File): Promise<ImageDimensions> => {
  const fileDataUrl = await readFileAsDataUrl(file);
  return calculateImageDimensionsUrl(fileDataUrl);
};

export const getDataTransferFilesAsDataUrls = (
  dataTransfer: DataTransfer
): Promise<{ type: string; name: string; dataUrl: string }[]> => {
  if (!dataTransfer?.files || dataTransfer.files.length === 0) {
    return Promise.resolve([]);
  }

  const readFilePromises = [...dataTransfer.files].map(async (file: File) => {
    const dataUrl = await readFileAsDataUrl(file);
    return {
      type: file.type,
      name: file.name,
      dataUrl,
    };
  });

  return Promise.all(readFilePromises);
};

export const getQueryParams = (
  queryString: string = ''
): Record<string, string | string[] | undefined> =>
  parseQuery(queryString || window.location.search);

export const getQueryParamValue = (
  paramName: string,
  queryString: string = ''
): string | string[] | undefined => {
  const params = getQueryParams(queryString);
  return params[paramName];
};

export const getQueryParamSingleValue = (
  paramName: string,
  queryString: string = ''
): string | undefined => {
  const value = getQueryParamValue(paramName, queryString);

  if (Array.isArray(value)) {
    return value[0];
  }
  return value;
};

export const navigateToUrl = (url: string): void => {
  window.location.assign(url);
};

export const isInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    return true; // in case `top` is inaccessible, meaning we're in an iframe cross-origin
  }
};

interface OpenResourceParams {
  url: string;
  isImage: boolean;
}
export const openResourceInNewTab = ({ url, isImage }: OpenResourceParams): void => {
  if (isImage) {
    window.open(`${ROUTES.FILE_VIEWER}/image?url=${url}`, '_blank');
  } else {
    const urlObj = new URL(url);
    // replace + with %20 in pathname - specific issue with some urls
    const encodedPath = urlObj.pathname.replace(/\+/g, '%2B');
    window.open(urlObj.origin + encodedPath, '_blank', 'noopener,noreferrer');
  }
};
